import type { EventEntityType } from '@atlas/types'
import { useSyncExternalStore } from 'react'
import { createClientId } from '@/lib/client-id'

// Module store for user-facing notifications, following `auth/auth-error-store.ts`.
// The reason for the pattern rather than context: `notification.error(...)` has to
// be reachable from a mutation `onError`, a realtime handler, or a plain module —
// none of which can read a context. Components subscribe via `useNotifications`.

/** Severity of a notification, rendered by the status bar as a dot colour and a screen-reader label. */
export enum NotificationVariant {
	INFO = 'info',
	SUCCESS = 'success',
	WARNING = 'warning',
	ERROR = 'error',
}

/** An optional user-invokable action carried by a notification. */
export interface NotificationActionOption {
	label: string
	onPress: () => void
}

/**
 * Which domain object a notification is about. Purely a reference — the surface
 * never fetches from it; it lets a consumer correlate or deep-link.
 */
export interface NotificationEntityRef {
	type: EventEntityType | string
	id: string
}

/** Optional extras accepted by every `notification.*` raiser. */
export interface NotificationOptions {
	action?: NotificationActionOption
	entityRef?: NotificationEntityRef
}

/** A notification as retained in history and rendered by the surface. */
export interface NotificationEntry {
	id: string
	variant: NotificationVariant
	message: string
	/** Epoch milliseconds at which the notification was raised. */
	timestamp: number
	action?: NotificationActionOption
	entityRef?: NotificationEntityRef
}

/**
 * Retention cap for the in-memory history. A session can run for hours and raise
 * notifications the whole time, so the list is bounded rather than allowed to grow;
 * 50 is a starting figure chosen to comfortably cover "what just happened", not a
 * measured one.
 *
 * The cap exists only because this store is in memory and per-tab, so a reload
 * loses the history entirely. Persisting it (localStorage, or server-side for a
 * cross-device notification centre) would remove the need for a cap and is
 * tracked as a separate low-priority piece of work.
 */
export const NOTIFICATION_HISTORY_LIMIT = 50

// Oldest first, so the tail is the most recent. Replaced (never mutated) on every
// change: `useSyncExternalStore` compares snapshots by identity, and a mutated array
// would be the same reference and would not re-render.
let entries: readonly NotificationEntry[] = []
const listeners = new Set<() => void>()

function emit(next: readonly NotificationEntry[]): void {
	entries = next
	for (const listener of listeners) listener()
}

/**
 * Reads the current history outside React.
 *
 * @returns {readonly NotificationEntry[]} Retained notifications, oldest first.
 */
export function getNotifications(): readonly NotificationEntry[] {
	return entries
}

/**
 * Raises a notification, trimming the oldest entries once the cap is exceeded.
 *
 * @param {NotificationVariant} variant - Severity of the notification.
 * @param {string} message - Human-readable text shown to the user.
 * @param {NotificationOptions} [options] - Optional action and entity reference.
 * @returns {string} The id of the created entry, usable with {@link dismissNotification}.
 */
function raise(variant: NotificationVariant, message: string, options?: NotificationOptions): string {
	const entry: NotificationEntry = {
		id: createClientId(),
		variant,
		message,
		timestamp: Date.now(),
		action: options?.action,
		entityRef: options?.entityRef,
	}
	unreadCount += 1
	const appended = [...entries, entry]
	emit(appended.length > NOTIFICATION_HISTORY_LIMIT ? appended.slice(-NOTIFICATION_HISTORY_LIMIT) : appended)
	return entry.id
}

/**
 * Raises notifications from anywhere in the application — a component, a hook, a
 * mutation callback, or a plain module with no React context available.
 *
 * @example
 * ```ts
 * notification.error('Could not request the validation asset.', {
 *   action: { label: 'Retry', onPress: retry },
 * })
 * ```
 */
export const notification = {
	info: (message: string, options?: NotificationOptions): string => raise(NotificationVariant.INFO, message, options),
	success: (message: string, options?: NotificationOptions): string =>
		raise(NotificationVariant.SUCCESS, message, options),
	warning: (message: string, options?: NotificationOptions): string =>
		raise(NotificationVariant.WARNING, message, options),
	error: (message: string, options?: NotificationOptions): string => raise(NotificationVariant.ERROR, message, options),
} as const

/**
 * Removes one notification, leaving the rest untouched. A no-op for an unknown id,
 * so a double dismiss (pointer plus keyboard, or two renders) is safe.
 *
 * @param {string} id - Id of the entry to remove.
 */
export function dismissNotification(id: string): void {
	const next = entries.filter((entry) => entry.id !== id)
	if (next.length === entries.length) return
	emit(next)
}

/** Removes every retained notification. */
export function clearNotifications(): void {
	if (entries.length === 0) return
	unreadCount = 0
	emit([])
}

/**
 * Notifications raised since the history was last opened.
 *
 * Tracked as a count rather than a per-entry read flag: the badge only ever needs
 * "how many since you last looked", and a flag per entry would have to be reconciled
 * with dismissal and the retention cap for no added benefit.
 */
let unreadCount = 0

/**
 * @returns {number} Notifications raised since the history was last marked read.
 */
export function getUnreadCount(): number {
	return unreadCount
}

/**
 * Clears the unread badge. Called when the history panel is opened — a badge that
 * cannot reach zero is trained-to-ignore within a day.
 */
export function markNotificationsRead(): void {
	if (unreadCount === 0) return
	unreadCount = 0
	emit(entries)
}

/**
 * Subscribes a component to the unread count.
 *
 * @returns {number} Current unread count.
 */
export function useUnreadNotificationCount(): number {
	return useSyncExternalStore(
		(cb) => {
			listeners.add(cb)
			return () => {
				listeners.delete(cb)
			}
		},
		getUnreadCount,
		getUnreadCount,
	)
}

/**
 * Subscribes a component to the notification history.
 *
 * @returns {readonly NotificationEntry[]} Retained notifications, oldest first.
 */
export function useNotifications(): readonly NotificationEntry[] {
	return useSyncExternalStore(
		(cb) => {
			listeners.add(cb)
			return () => {
				listeners.delete(cb)
			}
		},
		getNotifications,
		getNotifications,
	)
}
